/**
 * Cross-account portfolio chart aggregation for "All Accounts" mode. Each
 * account's own chart series can have a different history length/start date
 * and irregular gaps, so — unlike the single-account chart's bar-count-based
 * windowing (`portfolio-chart-window.ts`) — everything here aligns by TICK
 * TIMESTAMP, not by array index. This module is only used by the ALL-mode
 * chart path; single-account mode keeps using `portfolio-chart-window.ts`
 * unchanged.
 */

export interface PortfolioSeries {
  ticks: string[]
  values: number[]
}

interface SeriesPoint {
  tick: number
  value: number
}

/**
 * Zips `ticks[i]`/`values[i]` (defensively, up to the shorter of the two —
 * a ragged response shouldn't throw), drops any pair with a non-finite tick
 * or value, sorts ascending by tick, and dedupes same-tick points keeping the
 * LAST occurrence (a server response is expected to already be sorted/unique,
 * this is just a safety net).
 */
export function normalizePortfolioSeries(series: PortfolioSeries): SeriesPoint[] {
  const length = Math.min(series.ticks.length, series.values.length)
  const points: SeriesPoint[] = []
  for (let i = 0; i < length; i++) {
    const tick = Number(series.ticks[i])
    const value = series.values[i]
    if (!Number.isFinite(tick) || !Number.isFinite(value)) continue
    points.push({ tick, value })
  }
  points.sort((a, b) => a.tick - b.tick)

  const deduped: SeriesPoint[] = []
  for (const point of points) {
    if (deduped.length > 0 && deduped[deduped.length - 1].tick === point.tick) {
      deduped[deduped.length - 1] = point
    } else {
      deduped.push(point)
    }
  }
  return deduped
}

/**
 * Sums multiple accounts' portfolio series into one, aligned by tick
 * timestamp rather than array index (the series are not guaranteed to share
 * a tick grid — different accounts can have different history lengths, start
 * dates, and gaps). A series contributes 0 before its own first tick
 * (truthful: the combined portfolio really was worth less before that
 * account existed) and forward-fills its last known value across gaps and
 * past its own last tick (an account that stopped reporting still holds its
 * equity).
 *
 * Zero series returns an empty series. Exactly one series is returned
 * UNCHANGED (not even normalized) — this guarantees ALL-mode with a single
 * account produces byte-identical chart data to what that account's own
 * series already was.
 */
export function sumPortfolioSeries(series: PortfolioSeries[]): PortfolioSeries {
  if (series.length === 0) return { ticks: [], values: [] }
  if (series.length === 1) return series[0]

  const normalized = series.map(normalizePortfolioSeries).filter((points) => points.length > 0)
  if (normalized.length === 0) return { ticks: [], values: [] }
  if (normalized.length === 1) {
    return {
      ticks: normalized[0].map((p) => String(p.tick)),
      values: normalized[0].map((p) => p.value),
    }
  }

  const tickSet = new Set<number>()
  for (const points of normalized) {
    for (const point of points) tickSet.add(point.tick)
  }
  const grid = [...tickSet].sort((a, b) => a - b)

  const cursors = normalized.map(() => -1)
  const ticks: string[] = []
  const values: number[] = []

  for (const tick of grid) {
    let sum = 0
    for (let s = 0; s < normalized.length; s++) {
      const points = normalized[s]
      while (cursors[s] + 1 < points.length && points[cursors[s] + 1].tick <= tick) {
        cursors[s]++
      }
      if (cursors[s] >= 0) sum += points[cursors[s]].value
    }
    ticks.push(String(tick))
    values.push(sum)
  }

  return { ticks, values }
}

/**
 * Windows a (typically already-summed) series to the last `lookbackSeconds`
 * of ticks. `lookbackSeconds == null` keeps the whole series (matches
 * `windowPortfolioValues`'s ALL-timeframe behavior).
 *
 * Unlike a naive `keep ticks >= cutoff`, this synthesizes a point AT exactly
 * `cutoff` carrying the last known (forward-filled) value from before it, so
 * `windowed.values[0]` always represents the portfolio's value at the true
 * window start — otherwise, whenever a gap straddles the cutoff, the first
 * kept point could be from well after the window actually began, corrupting
 * any delta computed from `values.at(-1) - values[0]`.
 */
export function windowPortfolioSeriesByTick(
  series: PortfolioSeries,
  lookbackSeconds: number | null
): PortfolioSeries {
  if (lookbackSeconds == null) return series

  const points = normalizePortfolioSeries(series)
  if (points.length === 0) return { ticks: [], values: [] }

  const lastTick = points[points.length - 1].tick
  const cutoff = lastTick - lookbackSeconds

  let carryValue: number | null = null
  const kept: SeriesPoint[] = []
  for (const point of points) {
    if (point.tick < cutoff) {
      carryValue = point.value
      continue
    }
    if (point.tick === cutoff) {
      kept.push(point)
      carryValue = null
      continue
    }
    if (carryValue !== null) {
      kept.push({ tick: cutoff, value: carryValue })
      carryValue = null
    }
    kept.push(point)
  }
  // Every remaining point was skipped (all ticks < cutoff, i.e. the whole
  // series predates the window) — carry the last known value forward as a
  // single point at cutoff, so the caller sees "flat at the last known value"
  // rather than an empty window.
  if (kept.length === 0 && carryValue !== null) {
    kept.push({ tick: cutoff, value: carryValue })
  }

  return { ticks: kept.map((p) => String(p.tick)), values: kept.map((p) => p.value) }
}
